import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AdminStats {
  total_users: number;
  pending_users: number;
  active_users: number;
  trial_users: number;
  expired_users: number;
  suspended_users: number;
  total_feature_flags: number;
  enabled_feature_flags: number;
}

interface UseAdminStatsResult {
  stats: AdminStats | null;
  loading: boolean;
  fetchStats: () => Promise<void>;
}

export function useAdminStats(): UseAdminStatsResult {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_get_stats');

      if (error) {
        // If RPC doesn't exist (42883, PGRST202, or 404), fall back to direct table queries
        const isRpcMissing = error.code === '42883' || error.code === 'PGRST202' ||
          (error as { status?: number }).status === 404 || error.message?.includes('not found');
        if (isRpcMissing) {
          // RPC not found - fall back to direct table queries (silent)

          // Get user counts by status from accounts table
          const { data: accountsData, error: accountsError } = await supabase
            .from('accounts')
            .select('status');

          if (accountsError) throw accountsError;

          const accounts = accountsData || [];
          const statusCounts = accounts.reduce((acc, account) => {
            const status = account.status || 'unknown';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);

          // Get feature flag counts
          let totalFlags = 0;
          let enabledFlags = 0;

          try {
            const { data: flagsData } = await supabase
              .from('feature_flags')
              .select('enabled');

            if (flagsData) {
              totalFlags = flagsData.length;
              enabledFlags = flagsData.filter(f => f.enabled).length;
            }
          } catch {
            // Feature flags table might not exist, that's okay
            console.warn('feature_flags table not accessible');
          }

          setStats({
            total_users: accounts.length,
            pending_users: (statusCounts['pending'] || 0) + (statusCounts['pending_approval'] || 0),
            active_users: statusCounts['active'] || 0,
            trial_users: statusCounts['trial'] || 0,
            expired_users: statusCounts['expired'] || 0,
            suspended_users: statusCounts['suspended'] || 0,
            total_feature_flags: totalFlags,
            enabled_feature_flags: enabledFlags
          });
          return;
        }
        throw error;
      }

      const result = data as unknown as { success: boolean; stats: AdminStats; error?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch stats');
      }

      setStats(result.stats);
    } catch (err) {
      console.error('Failed to fetch admin stats:', err);
      toast.error('Failed to fetch admin stats');
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    stats,
    loading,
    fetchStats
  };
}
